import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  DollarSign, 
  TrendingUp, 
  AlertCircle,
  Download,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Info,
  Zap,
  BarChart3,
  Clock
} from 'lucide-react';
import platformApi from '../services/platformApi';

interface UsageSummary {
  organization: {
    id: string;
    currentUsageUnits: number;
    currentUsageCostCents: number;
    usageLimitUnits: number | null;
    planType: string;
    billingCycleStart: string;
  };
  period: {
    type: string;
    startDate: string;
    endDate: string;
  };
  usage: {
    breakdown: Array<{
      type: string;
      units: number;
      costCents: number;
      apiCalls: number;
    }>;
    dailyTrend: Array<{
      date: string;
      units: number;
      costCents: number;
      apiCalls: number;
    }>;
  };
}

interface Quotas {
  organization: {
    usageLimitUnits: number | null;
    currentUsageUnits: number;
    remainingUnits: number | null;
    planType: string;
    quotaResetDate: string;
    utilizationPercent: number;
  };
  planLimits: {
    usageLimitUnits: number | null;
    roomsPerMonth: number | null;
    participantMinutesPerMonth: number | null;
    features: string[];
  };
  recommendations: Array<{
    type: string;
    message: string;
    action: string;
  }>;
}

interface BillingEvent {
  id: string;
  eventType: string;
  details: any;
  timestamp: string;
}

export const BillingPage: React.FC = () => {
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [quotas, setQuotas] = useState<Quotas | null>(null);
  const [billingEvents, setBillingEvents] = useState<BillingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');

  useEffect(() => {
    fetchBillingData();
  }, [selectedPeriod]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      try {
        // Use platformApi service which handles authentication properly
        const [summaryData, quotasData, eventsData] = await Promise.all([
          platformApi.getUsageSummary(selectedPeriod),
          platformApi.getQuotas(),
          platformApi.getBillingEvents(10)
        ]);

        setUsageSummary(summaryData);
        setQuotas(quotasData);
        setBillingEvents(eventsData.events || []);

      } catch (apiError) {
        // If API calls fail (endpoints might not be implemented yet), show demo data
        console.log('Using demo data for billing display - endpoints not fully implemented yet');
        setUsageSummary(getDemoUsageSummary());
        setQuotas(getDemoQuotas());
        setBillingEvents(getDemoBillingEvents());
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing data');
      console.error('Billing data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDemoUsageSummary = (): UsageSummary => ({
    organization: {
      id: 'demo-org',
      currentUsageUnits: 847,
      currentUsageCostCents: 3420, // $34.20
      usageLimitUnits: 10000,
      planType: 'pro',
      billingCycleStart: '2025-08-01'
    },
    period: {
      type: selectedPeriod,
      startDate: new Date(2025, 7, 1).toISOString(),
      endDate: new Date().toISOString()
    },
    usage: {
      breakdown: [
        { type: 'room_creation', units: 15, costCents: 75, apiCalls: 15 },
        { type: 'token_generation', units: 45, costCents: 45, apiCalls: 45 },
        { type: 'participant_minute', units: 320, costCents: 1280, apiCalls: 32 },
        { type: 'api_call', units: 467, costCents: 47, apiCalls: 467 }
      ],
      dailyTrend: [
        { date: '2025-08-01', units: 120, costCents: 480, apiCalls: 120 },
        { date: '2025-08-02', units: 95, costCents: 380, apiCalls: 95 },
        { date: '2025-08-03', units: 67, costCents: 268, apiCalls: 67 }
      ]
    }
  });

  const getDemoQuotas = (): Quotas => ({
    organization: {
      usageLimitUnits: 10000,
      currentUsageUnits: 847,
      remainingUnits: 9153,
      planType: 'pro',
      quotaResetDate: '2025-09-01',
      utilizationPercent: 8.5
    },
    planLimits: {
      usageLimitUnits: 10000,
      roomsPerMonth: 100,
      participantMinutesPerMonth: 1000,
      features: ['Advanced video rooms', 'Priority support', 'Analytics']
    },
    recommendations: [
      {
        type: 'info',
        message: 'Your usage is well within limits. Consider the enterprise plan for unlimited usage.',
        action: 'upgrade_to_enterprise'
      }
    ]
  });

  const getDemoBillingEvents = (): BillingEvent[] => ([
    {
      id: 'evt_1',
      eventType: 'api_usage_spike',
      details: { usage: 150, threshold: 100 },
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'evt_2',
      eventType: 'quota_updated',
      details: { newLimit: 10000, oldLimit: 5000 },
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
    }
  ]);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  };

  const formatBillingType = (type: string) => {
    const types: { [key: string]: string } = {
      'room_creation': 'Room Creation',
      'token_generation': 'Token Generation',
      'participant_minute': 'Participant Minutes',
      'api_call': 'API Calls'
    };
    return types[type] || type;
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'usage_limit_exceeded':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'quota_updated':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'api_usage_spike':
        return <TrendingUp className="w-4 h-4 text-orange-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getRecommendationIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-light-text-secondary dark:text-dark-text-secondary">Loading billing data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button 
            onClick={fetchBillingData} 
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary">
            Billing & Usage
          </h1>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mt-2">
            Monitor your API usage, costs, and billing information
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="current_month">Current Month</option>
            <option value="last_month">Last Month</option>
            <option value="last_7_days">Last 7 Days</option>
          </select>
          <button className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors">
            <Download size={20} className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Current Usage & Quota */}
      {quotas && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                  Current Usage
                </p>
                <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                  {quotas.organization.currentUsageUnits.toLocaleString()}
                </p>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
                  of {quotas.organization.usageLimitUnits?.toLocaleString() || 'unlimited'} units
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-primary-500" />
            </div>
            <div className="mt-4">
              <div className="w-full bg-light-bg-quaternary dark:bg-dark-bg-quaternary rounded-full h-2">
                <div 
                  className="h-2 bg-primary-500 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(quotas.organization.utilizationPercent, 100)}%` }}
                />
              </div>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
                {quotas.organization.utilizationPercent}% utilized
              </p>
            </div>
          </div>

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                  Current Cost
                </p>
                <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                  {formatCurrency(usageSummary?.organization.currentUsageCostCents || 0)}
                </p>
                <p className="text-sm text-success mt-1 flex items-center">
                  <TrendingUp size={12} className="mr-1" />
                  {selectedPeriod.replace('_', ' ')}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                  Plan Type
                </p>
                <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1 capitalize">
                  {quotas.organization.planType}
                </p>
                <p className="text-sm text-info mt-1 flex items-center">
                  <Zap size={12} className="mr-1" />
                  {quotas.planLimits.features.length} features
                </p>
              </div>
              <CreditCard className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                  Quota Reset
                </p>
                <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                  {new Date(quotas.organization.quotaResetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1 flex items-center">
                  <Clock size={12} className="mr-1" />
                  Next billing cycle
                </p>
              </div>
              <Calendar className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>
      )}

      {/* Usage Breakdown */}
      {usageSummary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
            <div className="p-6 border-b border-light-border dark:border-dark-border">
              <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                Usage Breakdown by Type
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {usageSummary.usage.breakdown.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b border-light-border dark:border-dark-border last:border-b-0">
                    <div>
                      <p className="font-medium text-light-text-primary dark:text-dark-text-primary">
                        {formatBillingType(item.type)}
                      </p>
                      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                        {item.units.toLocaleString()} units • {item.apiCalls.toLocaleString()} calls
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-light-text-primary dark:text-dark-text-primary">
                        {formatCurrency(item.costCents)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
            <div className="p-6 border-b border-light-border dark:border-dark-border">
              <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                Daily Usage Trend
              </h2>
            </div>
            <div className="p-6">
              <div className="h-48 flex items-end justify-between space-x-2">
                {usageSummary.usage.dailyTrend.map((day, index) => {
                  const maxCost = Math.max(...usageSummary.usage.dailyTrend.map(d => d.costCents));
                  const height = (day.costCents / maxCost) * 100;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-primary-500 rounded-t opacity-80 hover:opacity-100 transition-opacity cursor-pointer relative group"
                        style={{ height: `${height}%` }}
                      >
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-dark-bg-primary dark:bg-light-bg-primary text-dark-text-primary dark:text-light-text-primary text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                            {formatCurrency(day.costCents)}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-2">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations & Billing Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recommendations */}
        {quotas && quotas.recommendations.length > 0 && (
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
            <div className="p-6 border-b border-light-border dark:border-dark-border">
              <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
                Recommendations
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {quotas.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg">
                    {getRecommendationIcon(rec.type)}
                    <div className="flex-1">
                      <p className="text-sm text-light-text-primary dark:text-dark-text-primary">
                        {rec.message}
                      </p>
                      <button className="text-xs text-primary-500 hover:text-primary-600 mt-1">
                        Take Action →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Recent Billing Events */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
          <div className="p-6 border-b border-light-border dark:border-dark-border">
            <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
              Recent Billing Events
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {billingEvents.length > 0 ? billingEvents.map((event, index) => (
                <div key={index} className="flex items-center space-x-3 py-3 border-b border-light-border dark:border-dark-border last:border-b-0">
                  {getEventIcon(event.eventType)}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                      {event.eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                      {new Date(event.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary text-center py-4">
                  No billing events to display
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Plan Features */}
      {quotas && (
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
          <div className="p-6 border-b border-light-border dark:border-dark-border">
            <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
              Current Plan Features
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quotas.planLimits.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-light-text-primary dark:text-dark-text-primary">
                    {feature}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                  Need more features or higher limits?
                </p>
              </div>
              <button className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors">
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};